import { useEffect, useRef, useState } from "react";
import { useTenant } from "../../../Contexts/TenantContext";
import { orderService } from "../../../Api/Services/orderService";
import { customerService } from "../../../Api/Services/customerService";
import { createOrderHubConnection } from "../../../Api/orderHub";
import { SimplifiedOrder, UpdateShippingDetails } from "../../../Models/Order";
import { ShippingDetails } from "../../../Models/ShippingDetails";
import { OrderStatus } from "../../../Enums/OrderStatus";
import { OrderSource } from "../../../Enums/OrderSource";
import { NfeStatus } from "../../../Enums/NfeStatus";
import { getOrderStatusLabel } from "../../../Utils/Enums";
import { formatCnpj, formatCpf, formatDateTimeToBR } from "../../../Utils/DataFormatters";
import { FaBoxOpen } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import PageTitle from "../../../Components/PageTitle/PageTitle";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import OrderCard from "../../../Components/OrderCard/OrderCard";
import ShippingDetailsModal from "../../../Components/ShippingDetailsModal/ShippingDetailsModal";
import UpdateAvailabilityModal from "../../../Components/UpdateAvailabilityModal/UpdateAvailabilityModal";
import Pagination from "../../../Components/Pagination/Pagination";
import PaginationSummary from "../../../Components/Pagination/PaginationSummary";
import Button from "../../../Components/Button/Button";

const AdminOrderPage = () => {
  const { tenantPreference, tenantShippingProviderConfig } = useTenant();
  const NO_MATCH_CUSTOMER = "__NO_MATCH_CUSTOMER__";
  const [orders, setOrders] = useState<SimplifiedOrder[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const ordersPerPage = 8;
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hubRef = useRef<ReturnType<typeof createOrderHubConnection> | null>(null);

  const [isShippingDetailsModalOpen, setShippingDetailsModalOpen] = useState(false);
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [shippingDetails, setShippingDetails] = useState<ShippingDetails | null>(null);
  const [emittingNfeOrderId, setEmittingNfeOrderId] = useState<string | null>(null);
  const [cancellingNfeOrderId, setCancellingNfeOrderId] = useState<string | null>(null);
  const [isAvailabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [availabilityOrderId, setAvailabilityOrderId] = useState<string | null>(null);
  const [availabilityOrderNumber, setAvailabilityOrderNumber] = useState<number | null>(null);
  const [availabilityLeadTime, setAvailabilityLeadTime] = useState<number | "">("");
  const [isSavingAvailability, setIsSavingAvailability] = useState(false);
  const [isSavingShippingDetails, setIsSavingShippingDetails] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const customerSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState<string | null>(null);
  const [selectedCustomerDocument, setSelectedCustomerDocument] = useState<string | null>(null);

  useEffect(() => {
    if (selectedCustomerId === NO_MATCH_CUSTOMER) {
      return;
    }
    loadOrders(currentPage, selectedStatus, selectedCustomerId);
  }, [currentPage, selectedStatus, selectedCustomerId]);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
        }
      } else {
        startPolling();
        refreshOrders();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    startPolling();
    startHub();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
      if (hubRef.current) {
        hubRef.current.stop();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedStatus]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const loadOrders = async (page: number = 1, status?: OrderStatus | null, customerId?: string | null) => {
    setIsFetching(true);
    try {
      const data = await orderService.getAll({
        page,
        pageSize: ordersPerPage,
        status: status ?? null,
        customerId: customerId ?? null,
        orderSource: OrderSource.Online
      });

      setOrders(data.items);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalItems);
      setLastUpdated(new Date());
    } finally {
      setIsFetching(false);
      setIsInitialLoading(false);
    }
  };

  const refreshOrders = async () => {
    if (isFetching || isRefreshing) {
      return;
    }
    if (selectedCustomerId === NO_MATCH_CUSTOMER) {
      setOrders([]);
      setTotalItems(0);
      setTotalPages(1);
      return;
    }
    setIsRefreshing(true);
    await loadOrders(currentPage, selectedStatus, selectedCustomerId);
    setIsRefreshing(false);
  };

  const startPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
    }

    pollRef.current = setInterval(() => {
      if (!document.hidden) {
        refreshOrders();
      }
    }, 60000);
  };

  const handleDownloadPrePostingLabel = async (orderId: string, orderNumber: number) => {
    try {
      const blob = await orderService.downloadPrePostingLabel(orderId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `rotulo-correios-pedido-${orderNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Não foi possível baixar o rótulo agora. Tente novamente em alguns instantes.");
    }
  };

  const handleRequestPrePostingLabel = async (orderId: string) => {
    try {
      const ok = await orderService.requestPrePostingLabel(orderId);
      if (!ok) {
        toast.error("Não foi possível solicitar o rótulo no momento.");
      }
      await refreshOrders();
    } catch {
      toast.error("Não foi possível solicitar o rótulo no momento.");
    }
  };

  const [emittingPrePostingOrderIds, setEmittingPrePostingOrderIds] = useState<Set<string>>(new Set());

  const handleEmitPrePosting = async (orderId: string, data: { packagingType: number; weightGrams: number; lengthCm: number; widthCm: number; heightCm: number }) => {
    setEmittingPrePostingOrderIds(prev => new Set(prev).add(orderId));
    try {
      const ok = await orderService.emitPrePosting(orderId, data);
      if (!ok) {
        throw new Error("Não foi possível emitir o cartão de postagem.");
      }
      await refreshOrders();
    } catch {
      toast.error("Não foi possível emitir o cartão de postagem. Tente novamente.");
    }
    setEmittingPrePostingOrderIds(prev => {
      const next = new Set(prev);
      next.delete(orderId);
      return next;
    });
  };

  const searchCustomers = async (search: string) => {
    const term = search.trim();
    if (customerSearchTimeout.current) {
      clearTimeout(customerSearchTimeout.current);
    }
    customerSearchTimeout.current = setTimeout(async () => {
      if (!term) {
        setSelectedCustomerId(null);
        setSelectedCustomerName(null);
        setSelectedCustomerDocument(null);
        setCurrentPage(1);
        await loadOrders(1, selectedStatus, null);
        return;
      }

      try {
        setIsSearchingCustomers(true);
        const result = await customerService.getAll({ page: 1, pageSize: 1, search: term });
        const match = result.items[0];
        if (match) {
          const name =
            match.customerType === 0
              ? match.individualPerson?.fullName ?? ""
              : match.legalEntity?.companyName ?? "";
          const documentRaw =
            match.customerType === 0
              ? match.individualPerson?.cpf ?? ""
              : match.legalEntity?.cnpj ?? "";
          const document =
            match.customerType === 0
              ? `CPF: ${formatCpf(documentRaw)}`
              : `CNPJ: ${formatCnpj(documentRaw)}`;
          setSelectedCustomerId(match.id);
          setSelectedCustomerName(name);
          setSelectedCustomerDocument(document);
          setCurrentPage(1);
          await loadOrders(1, selectedStatus, match.id);
        } else {
          setSelectedCustomerId(NO_MATCH_CUSTOMER);
          setSelectedCustomerName(null);
          setSelectedCustomerDocument(null);
          setOrders([]);
          setTotalItems(0);
          setTotalPages(1);
        }
      } catch {
        toast.error("Não foi possível buscar clientes.");
      } finally {
        setIsSearchingCustomers(false);
      }
    }, 300);
  };

  const handleCustomerInputChange = (value: string) => {
    setCustomerQuery(value);
    searchCustomers(value);
  };

  const clearCustomerFilter = () => {
    setSelectedCustomerId(null);
    setSelectedCustomerName(null);
    setSelectedCustomerDocument(null);
    setCustomerQuery("");
    setCurrentPage(1);
    loadOrders(1, selectedStatus, null);
  };

  const startHub = async () => {
    if (hubRef.current) {
      return;
    }

    const hub = createOrderHubConnection();
    hubRef.current = hub;

    hub.on("OrderStatusChanged", () => {
      refreshOrders();
    });

    try {
      await hub.start();
    } catch {
      // Ignore hub start errors; polling + manual refresh still work
    }
  };

  const handleSelectStatus = (status: OrderStatus | null) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    if (selectedCustomerId === NO_MATCH_CUSTOMER) {
      setOrders([]);
      setTotalItems(0);
      setTotalPages(1);
      return;
    }
    loadOrders(1, status, selectedCustomerId);
  };

  const handleCancelOrder = async (orderId: string) => {
    try {
      const canceled = await orderService.cancel({ id: orderId });
      if (canceled) {
        toast.success("Pedido cancelado com sucesso!");
        await loadOrders(currentPage);
      }
    } catch {
      toast.error("Erro ao cancelar o pedido, tente novamente mais tarde.");
    }
  };

  const handleEditShippingDetails = (orderNumber: number, shippingDetails: ShippingDetails) => {
    setOrderNumber(orderNumber);
    setShippingDetails(shippingDetails);
    setShippingDetailsModalOpen(true);
  };

  const resetAvailabilityState = () => {
    setAvailabilityModalOpen(false);
    setAvailabilityOrderId(null);
    setAvailabilityOrderNumber(null);
    setAvailabilityLeadTime("");
  };

  const handleOpenAvailabilityModal = (
    orderId: string,
    orderNumber: number,
    maxLeadTimeDays?: number | null
  ) => {
    setAvailabilityOrderId(orderId);
    setAvailabilityOrderNumber(orderNumber);
    setAvailabilityLeadTime(maxLeadTimeDays ?? "");
    setAvailabilityModalOpen(true);
  };

  const handleSaveAvailability = async () => {
    if (!availabilityOrderId) {
      return;
    }

    try {
      setIsSavingAvailability(true);
      const leadTimeValue = availabilityLeadTime === "" ? null : Number(availabilityLeadTime);
      const updated = await orderService.updateAvailability(availabilityOrderId, leadTimeValue);

      if (updated) {
        toast.success("Disponibilidade atualizada com sucesso!");
        resetAvailabilityState();
        await loadOrders(currentPage, selectedStatus);
      }
    } catch {
      toast.error("Erro ao atualizar disponibilidade. Tente novamente mais tarde.");
    } finally {
      setIsSavingAvailability(false);
    }
  };

  const handleSaveShippingDetails = async (data: UpdateShippingDetails) => {
    try {
      setIsSavingShippingDetails(true);
      const updated = await orderService.updateShippingDetails(data);
      if (updated) {
        toast.success("Dados de envio atualizados com sucesso!");
        await loadOrders(currentPage);
        setShippingDetailsModalOpen(false);
      }
    } catch {
      toast.error("Erro ao atualizar dados de envio, tente novamente mais tarde!");
    } finally {
      setIsSavingShippingDetails(false);
    }
  };

  const handleEmitNfe = async (orderId: string) => {
    setEmittingNfeOrderId(orderId);
    try {
      const result = await orderService.emitNfe(orderId);
      if (result.success && result.status === NfeStatus.Authorized) {
        toast.success("NF-e emitida com sucesso!");
      }
      await loadOrders(currentPage, selectedStatus);
    } finally {
      setEmittingNfeOrderId(null);
    }
  };

  const handleCancelNfe = async (orderId: string, justification: string) => {
    setCancellingNfeOrderId(orderId);
    try {
      const result = await orderService.cancelNfe(orderId, justification);
      if (result.success && result.status === NfeStatus.Cancelled) {
        toast.success("NF-e cancelada com sucesso!");
      } else if (result.providerMessage) {
        toast.error(result.providerMessage);
      } else {
        toast.error("Não foi possível cancelar a NF-e.");
      }
      await loadOrders(currentPage, selectedStatus);
    } finally {
      setCancellingNfeOrderId(null);
    }
  };

  const isStatusFilterable = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.ProcessingPayment:
        return false;
      case OrderStatus.AwaitingAvailability:
        return tenantPreference?.enableMadeToOrderProducts ?? false;
      case OrderStatus.AwaitingShippingInfo:
        return tenantShippingProviderConfig?.offerCustomCarrier ?? false;
      default:
        return true;
    }
  };

  // Stays visible on an empty result, otherwise the filter that caused it could not be undone
  const showFilters = totalItems > 0 || customerQuery.length > 0 || selectedCustomerId !== null || selectedStatus !== null;

  return (
    <div className={`bg-gray-50 min-h-screen flex flex-col`}>
      <SmartNavbar />
      <div className={`p-4 sm:p-14 flex-1 flex flex-col`}>
        <div className="flex items-center justify-between gap-3 my-6 sm:my-0 sm:mb-8">
          <PageTitle>PEDIDOS ONLINE</PageTitle>
          {isInitialLoading && orders.length === 0 ? (
            <div className="flex items-center gap-3">
              <div className="hidden sm:block h-4 w-28 bg-gray-200 rounded animate-pulse" />
              <div className="h-9 w-28 bg-gray-200 rounded animate-pulse" />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="hidden sm:block text-sm text-gray-600">
                {lastUpdated ? `Atualizado às ${formatDateTimeToBR(lastUpdated)}` : "Carregando..."}
              </div>
              <Button variant="secondary"
                onClick={refreshOrders}
                disabled={isFetching || isRefreshing}
                className="px-3 py-2 text-sm"
              >
                Recarregar
              </Button>
            </div>
          )}
        </div>

        {showFilters && (
        <details
          className="border border-gray-300 rounded p-3 bg-white mb-4"
          open={filtersOpen}
          onToggle={e => setFiltersOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary className="font-medium cursor-pointer">FILTROS</summary>

          <div className="mt-4">
            <div>
              <h3 className="text-sm font-semibold mb-2">STATUS</h3>
              {isInitialLoading ? (
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div key={idx} className="h-9 w-32 bg-gray-200 rounded-full animate-pulse" />
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleSelectStatus(null)}
                    className={`px-3 py-1 rounded-full text-sm font-medium border ${
                      selectedStatus === null
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                    }`}
                  >
                    Todos os pedidos
                  </button>
                  {Object.values(OrderStatus)
                    .filter(value => typeof value === "number" && isStatusFilterable(value))
                    .map(status => (
                      <button
                        key={status}
                        onClick={() => handleSelectStatus(status as OrderStatus)}
                        className={`px-3 py-1 rounded-full text-sm font-medium border ${
                          selectedStatus === status
                            ? "bg-primary-600 text-white border-primary-600"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-primary-100"
                        }`}
                      >
                        {getOrderStatusLabel(status as OrderStatus)}
                      </button>
                    ))}
                </div>
              )}
            </div>
          </div>
        </details>
        )}

        {showFilters && (
          <div className="w-full sm:max-w-2xl mb-4">
            <h3 className="text-sm font-semibold mb-2">PESQUISA</h3>
            <div className="space-y-2">
              <SearchBar
                onSearch={handleCustomerInputChange}
                className="w-full py-3"
                placeholder="Buscar por nome ou documento do cliente..."
                value={customerQuery}
                borderClass="border-gray-300"
                iconClass="text-primary-600"
              />
              {isSearchingCustomers && (
                <div className="text-xs text-gray-500">Buscando clientes...</div>
              )}
              {selectedCustomerId && selectedCustomerName && (
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-50 text-primary-700 text-xs rounded-full border border-primary-100">
                  {selectedCustomerName}
                  {selectedCustomerDocument ? ` • ${selectedCustomerDocument}` : ""}
                  <button onClick={clearCustomerFilter} className="font-semibold hover:underline">
                    Remover
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {(isInitialLoading || isRefreshing) && (
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="bg-white border border-gray-300 rounded p-4 animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-32" />
                <div className="h-3 bg-gray-200 rounded w-48" />
                <div className="h-3 bg-gray-200 rounded w-full" />
                <div className="h-3 bg-gray-200 rounded w-5/6" />
              </div>
            ))}
          </div>
        )}

        {!isInitialLoading && !isRefreshing && orders.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
            <FaBoxOpen className="text-3xl" />
            <p className="text-center text-sm sm:text-base">
              {selectedStatus === null
                ? "Nenhum pedido encontrado"
                : `Nenhum pedido com status "${getOrderStatusLabel(selectedStatus)}" encontrado`}
            </p>
          </div>
        ) : !isInitialLoading && !isRefreshing && (
          orders.map(order => (
            <OrderCard
              key={order.id}
              id={order.id}
              orderNumber={order.orderNumber}
              status={order.status}
              finalPrice={order.finalPrice}
              shippingMethod={order.shippingMethod}
              shippingDetails={order.shippingDetails}
              createdAt={order.createdAt}
              paymentMethod={order.paymentMethod}
              paymentReceivedAt={order.paymentReceivedAt}
              nfe={order.nfe}
              customerId={order.customerId}
              customerName={order.customerName ?? undefined}
              customerDocument={order.customerDocument ?? undefined}
              customerType={order.customerType}
              hasMadeToOrderItems={order.hasMadeToOrderItems}
              maxMadeToOrderLeadTimeDays={order.maxMadeToOrderLeadTimeDays}
              enablePrePostingLabel={tenantShippingProviderConfig?.enablePrePostingLabel ?? false}
              showXmlDownload={tenantPreference?.enableNfeXmlDownload ?? true}
              onCancel={handleCancelOrder}
              onEditShippingDetailsModal={handleEditShippingDetails}
              onDownloadPrePostingLabel={handleDownloadPrePostingLabel}
              onRequestPrePostingLabel={handleRequestPrePostingLabel}
              onEmitPrePosting={(orderId, data) => handleEmitPrePosting(orderId, data)}
              isEmittingPrePosting={emittingPrePostingOrderIds.has(order.id)}
              onEmitNfe={() => handleEmitNfe(order.id)}
              isEmittingNfe={emittingNfeOrderId === order.id}
              onCancelNfe={(justification: string) => handleCancelNfe(order.id, justification)}
              isCancellingNfe={cancellingNfeOrderId === order.id}
              onUpdateAvailability={handleOpenAvailabilityModal}
            />
          ))
        )}

        {orders.length > 0 && !isFetching && (
          <>
            <PaginationSummary
              totalItems={totalItems}
              singularLabel="pedido encontrado"
              pluralLabel="pedidos encontrados"
            />

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      <UpdateAvailabilityModal
        isOpen={isAvailabilityModalOpen}
        orderNumber={availabilityOrderNumber}
        leadTimeDays={availabilityLeadTime}
        onChangeLeadTime={value => setAvailabilityLeadTime(value)}
        onClose={resetAvailabilityState}
        onSave={handleSaveAvailability}
        isSaving={isSavingAvailability}
      />

      {isShippingDetailsModalOpen && shippingDetails && orderNumber && (
        <ShippingDetailsModal
          orderNumber={orderNumber}
          shippingDetails={shippingDetails}
          isOpen={isShippingDetailsModalOpen}
          onClose={() => setShippingDetailsModalOpen(false)}
          onSave={handleSaveShippingDetails}
          isSaving={isSavingShippingDetails}
        />
      )}

    </div>
  );
};

export default AdminOrderPage;
